import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, Search, Copy, FolderOpen, X, Send, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUS_OPTS = [
  { value: "pending", label: "In attesa" },
  { value: "received", label: "Ricevuto" },
  { value: "incomplete", label: "Incompleto" },
  { value: "to_verify", label: "Da verificare" },
];
const statusColor = {
  pending: "bg-beige text-petrol", received: "bg-green-100 text-green-800",
  incomplete: "bg-amber-100 text-amber-800", to_verify: "bg-blue-100 text-blue-800",
};

export default function AdminCheckins() {
  const [checkins, setCheckins] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [q, setQ] = useState("");
  const [showGen, setShowGen] = useState(false);
  const [gen, setGen] = useState({ apartment_id: "", guest_name: "", email: "", arrival_date: "", departure_date: "" });
  const [genLink, setGenLink] = useState("");

  const load = () => api.get("/admin/checkins", { params: { q } }).then((r) => setCheckins(r.data));
  useEffect(() => { load(); }, [q]);
  useEffect(() => { api.get("/admin/apartments").then((r) => setApartments(r.data)); }, []);

  const generate = async () => {
    if (!gen.apartment_id || !gen.arrival_date || !gen.departure_date) { toast.error("Compila appartamento e date"); return; }
    try {
      const r = await api.post("/admin/checkins/generate-link", gen);
      const link = `${window.location.origin}/checkin/${r.data.token}`;
      setGenLink(link);
      toast.success("Link generato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const updateStatus = async (id, doc_status) => {
    await api.put(`/admin/checkins/${id}/status`, { doc_status });
    toast.success("Stato documento aggiornato");
    load();
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/checkins/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "checkin_export.csv"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore esportazione"); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiato"); };

  const removeCheckin = async (c) => {
    const label = c.completed ? `${c.last_name} ${c.first_name}` : c.apartment_name;
    if (!window.confirm(`Eliminare definitivamente la richiesta di check-in${label ? ` di ${label}` : ""}? L'operazione non è reversibile.`)) return;
    try {
      await api.delete(`/admin/checkins/${c.id}`);
      toast.success("Richiesta di check-in eliminata");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendPrearrival = async (c) => {
    if (!c.email) { toast.error("Nessuna email associata a questo check-in"); return; }
    try {
      const checkin_link = `${window.location.origin}/checkin/${c.token}`;
      const r = await api.post(`/admin/checkins/${c.id}/prearrival`, { checkin_link });
      toast.success(r.data.sent ? `Email pre-arrivo inviata a ${r.data.email}` : `Email registrata (SMTP non configurato): ${r.data.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-checkins">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif font-light text-4xl text-petrol">Check-in</h1>
          <p className="text-muted-foreground">Gestione check-in online e documenti</p>
        </div>
        <div className="flex gap-3">
          <button onClick={exportCsv} data-testid="export-csv" className="flex items-center gap-2 border border-petrol text-petrol px-4 py-2.5 rounded-sm hover:bg-beige transition-colors text-sm">
            <Download size={16} /> CSV
          </button>
          <button onClick={() => { setShowGen(true); setGenLink(""); }} data-testid="generate-link-btn" className="flex items-center gap-2 bg-petrol text-white px-4 py-2.5 rounded-sm hover:bg-[#1A3A41] transition-colors text-sm">
            <Plus size={16} /> Genera link
          </button>
        </div>
      </div>

      <div className="relative mb-6 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome o email" data-testid="checkin-search"
          className="w-full border border-line rounded-sm pl-9 pr-3 py-2.5 text-sm text-petrol bg-card outline-none focus:border-petrol" />
      </div>

      <div className="bg-card border border-line rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-beige text-petrol/70">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Ospite</th>
              <th className="text-left px-4 py-3 font-medium">Appartamento</th>
              <th className="text-left px-4 py-3 font-medium">Arrivo</th>
              <th className="text-left px-4 py-3 font-medium">Documenti</th>
              <th className="text-left px-4 py-3 font-medium">Stato</th>
              <th className="text-left px-4 py-3 font-medium">Cartella</th>
              <th className="text-left px-4 py-3 font-medium">Pre-arrivo</th>
              <th className="text-left px-4 py-3 font-medium">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {checkins.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Nessun check-in.</td></tr>}
            {checkins.map((c) => (
              <tr key={c.id} className="border-t border-line" data-testid={`checkin-row-${c.id}`}>
                <td className="px-4 py-3 text-petrol">
                  {c.completed ? `${c.last_name} ${c.first_name}` : <span className="text-muted-foreground italic">In attesa</span>}
                  {c.email && <div className="text-xs text-muted-foreground">{c.email}</div>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{c.apartment_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.arrival_date}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.documents?.length || 0}</td>
                <td className="px-4 py-3">
                  <select value={c.doc_status} onChange={(e) => updateStatus(c.id, e.target.value)} data-testid={`checkin-status-${c.id}`}
                    className={`rounded-sm px-2 py-1 text-xs outline-none border border-line ${statusColor[c.doc_status] || ""}`}>
                    {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  {c.folder_path ? (
                    <button onClick={() => copy(c.folder_path)} title={c.folder_path} className="flex items-center gap-1 text-petrol hover:text-gold text-xs">
                      <FolderOpen size={14} /> Apri
                    </button>
                  ) : <span className="text-muted-foreground/50 text-xs">—</span>}
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => sendPrearrival(c)} disabled={!c.email} data-testid={`send-prearrival-${c.id}`}
                    title={c.email ? "Invia info pre-arrivo" : "Nessuna email"}
                    className="flex items-center gap-1 text-petrol hover:text-gold text-xs disabled:opacity-40 disabled:cursor-not-allowed">
                    <Send size={14} /> {c.prearrival_sent_at ? "Reinvia" : "Invia"}
                  </button>
                  {c.prearrival_sent_at && <div className="text-[10px] text-green-700 mt-0.5">Inviata {c.prearrival_sent_at.slice(0, 10)}</div>}
                  {c.reminder_sent_at && <div className="text-[10px] text-amber-700 mt-0.5">Sollecito {c.reminder_sent_at.slice(0, 10)}</div>}
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => removeCheckin(c)} data-testid={`delete-checkin-${c.id}`}
                    title="Elimina richiesta di check-in" className="flex items-center gap-1 text-destructive hover:opacity-70 text-xs">
                    <Trash2 size={14} /> Elimina
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Generate link modal */}
      {showGen && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center px-4" onClick={() => setShowGen(false)}>
          <div className="bg-cream rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="generate-link-modal">
            <div className="flex items-center justify-between p-5 border-b border-line">
              <h2 className="font-serif text-2xl text-petrol">Genera link check-in</h2>
              <button onClick={() => setShowGen(false)}><X size={20} className="text-muted-foreground" /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Appartamento *</span>
                <select value={gen.apartment_id} onChange={(e) => setGen({ ...gen, apartment_id: e.target.value })} data-testid="gen-apartment"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none">
                  <option value="">Seleziona...</option>
                  {apartments.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Cognome ospite</span>
                <input value={gen.guest_name} onChange={(e) => setGen({ ...gen, guest_name: e.target.value })} data-testid="gen-guest"
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <label className="block">
                <span className="overline text-[10px] text-muted-foreground">Email ospite</span>
                <input value={gen.email} onChange={(e) => setGen({ ...gen, email: e.target.value })}
                  className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline text-[10px] text-muted-foreground">Arrivo *</span>
                  <input type="date" value={gen.arrival_date} onChange={(e) => setGen({ ...gen, arrival_date: e.target.value })} data-testid="gen-arrival"
                    className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
                </label>
                <label className="block">
                  <span className="overline text-[10px] text-muted-foreground">Partenza *</span>
                  <input type="date" value={gen.departure_date} onChange={(e) => setGen({ ...gen, departure_date: e.target.value })} data-testid="gen-departure"
                    className="w-full border border-line rounded-sm px-3 py-2 mt-1 text-sm text-petrol bg-card outline-none" />
                </label>
              </div>
              {genLink && (
                <div className="bg-card border border-line rounded-sm p-3 flex items-center gap-2">
                  <span className="text-xs text-petrol truncate flex-1" data-testid="generated-link">{genLink}</span>
                  <button onClick={() => copy(genLink)} className="text-gold"><Copy size={16} /></button>
                </div>
              )}
              <button onClick={generate} data-testid="gen-submit" className="w-full bg-petrol text-white py-3 rounded-sm hover:bg-[#1A3A41] transition-colors">
                Genera link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
